/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

//@Packages
import {
  Button, Card, CardContent,
  CardHeader, Divider, PageLoader
} from "@forge/common";
import { useTranslation } from "react-i18next";

//@Components
import Toast from "../../../reusablecomponents/toast";
import FileDownload, { FileProps } from "../file-download/FileDownload";
import ConfirmationModal from "../../../reusablecomponents/confirmationmodal";
import { downloadFile } from "../../../services/file-service-api/fileDownloader";

//@Models
import { FileDetailsEntity } from "../../../models/diagnosticDetails";

//@Services
import {
  useGetDownFileSasUriQuery, useGetMetaDataApiQuery, useSendCommandMutation
} from "../../../services/file-service-api/fileServeApi";

//@Utils
import { getStatusClass } from "../utils/getStatusClass";
import { convertUTCStringtoFormattedDate } from "../../../utils/utility";
import { getErrorInfo } from "../../../reusablecomponents/errorInfo/getErrorInfo";

//@Constants
import { CommandServiceNetworkErrObj, NetworkErrObj } from "../utils/gatewayTypes";
import { LOCALE_CONST } from "../../../utils/constants";

/**
 * ConfigData component renders a ConfigData report for a gateway device.
 * It displays the last execution details, including trigger time and status,
 * and provides an option to run ConfigData again. and download the reports.
 *
 * @returns {JSX.Element} The rendered ConfigData component.
 */
const ConfigData: React.FC = () => {
  const { t } = useTranslation();

  const DELAY_TIMEOUT = 60000;

  const convertFn = (value: string) => t(value);

  const [status, setStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState<string>("");
  const [fileToDownload, setFileToDownload] = useState<string>("");

  const [showToast, setShowToast] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFetchDataModal, setShowFetchDataModal] = useState(false);

  const {
    data: configMetaData,
    error: configMetaDataErr,
    refetch: refetchConfigMetaData,
    isLoading: configMetaDataLoading,
    isFetching: isConfigMetaDataFetching
  } = useGetMetaDataApiQuery({
    systemGuid: sessionStorage.getItem("systemGuid") || "",
    category: "config"
  });

  const {
    data: downloadFileSasUri,
    error: downloadFileSasUriErr,
    isLoading: downloadSasUriLoading,
    isFetching: isDownloadFileSasUriFetching
  } = useGetDownFileSasUriQuery({
    systemGuid: sessionStorage.getItem("systemGuid") || "",
    category: "config",
    fileName: fileToDownload
  }, { skip: !fileToDownload });

  const [sendCommand,
    {
      data: fetchConfigData,
      error: fetchConfigDataError,
      isError: hasFetchConfigDataError
    }] = useSendCommandMutation();

  const handleDownload = async (sasUri: string) => {
    const { error } = await downloadFile(sasUri, fileToDownload);

    if (error) {
      setErrorTitle("Error");
      setErrorMessage(error);
      setShowToast(true);
    }
  };

  /**
   * useEffect hook to set the status based on the meta data response
   */
  /* istanbul ignore next */
  useEffect(() => {
    if (configMetaData && configMetaData.lastExecutionDetails) {
      setStatus(configMetaData.lastExecutionDetails.status);
    }
  }, [configMetaData]);

  /**
   * useEffect hook to refetch the config meta data right after fetch config (send command api call)
   */
  /* istanbul ignore next */
  useEffect(() => {
    const isFetchConfigInitiated =
      configMetaData?.lastExecutionDetails?.status === "Initiated";

    const isFetchConfigSuccess =
      configMetaData?.lastExecutionDetails?.status === "Success";

    const isFetchConfigFailed =
      configMetaData?.lastExecutionDetails?.status === "Failed";

    // Set the loading to false if the status is Initiated or Success or Failed
    if (isFetchConfigInitiated || isFetchConfigSuccess || isFetchConfigFailed) {
      setIsLoading(false);
    }

    if (fetchConfigData || isFetchConfigInitiated) {
      // Set interval to refetch the config meta data every 1 minute
      const interval = setInterval(async() => {
        try {
          await refetchConfigMetaData();
        } catch (error) { }

        if (
          configMetaData?.lastExecutionDetails?.status === "Success" ||
          configMetaData?.lastExecutionDetails?.status === "Failed"
        ) {
          clearInterval(interval);
        }
      }, DELAY_TIMEOUT); // 60,000 milliseconds = 1 minute

      return () => {
        clearInterval(interval); // Cleanup interval
      };
    }
  }, [
    configMetaData, fetchConfigData,
    hasFetchConfigDataError, fetchConfigDataError
  ]);

  /**
   * useEffect hook to download the file after getting the sas uri
   */
  /* istanbul ignore next */
  useEffect(() => {
    if (downloadFileSasUri) {
      handleDownload(downloadFileSasUri.sasUri);
      setFileToDownload("");
    }
  }, [downloadFileSasUri]);

  /**
   * To trigger send command api which sends the command to the IOT Device
   */
  const handleConfirm = async () => {
    setShowFetchDataModal(false);
    setIsLoading(true);
    try {
      await sendCommand({
        systemGuid: sessionStorage.getItem("systemGuid") || "",
        category: "config"
      }).unwrap();
    } catch (err) { }
  };

  /**
   * To set the error title, msg for toast
   * @param statusCode number
   */
  /* istanbul ignore next */
  const displayErrorToast = (statusCode: number, errMsg?: string, fetchConfig?: boolean) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      setErrorTitle(errObj.title);
      setErrorMessage(errMsg || errObj.description);
    } else {
      setErrorTitle(t(LOCALE_CONST.noRespReceived));
      setErrorMessage(t(LOCALE_CONST.noRespReceivedSystem));
    }
    setShowToast(true);
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  /**
   * metadata, command serve, download file  endpoint network error handling
   */
  /* istanbul ignore next */
  useEffect(() => {
    if (configMetaDataErr) {
      setIsLoading(false);
      displayErrorToast(
        (configMetaDataErr as NetworkErrObj)?.status,
        getErrorObjValue(configMetaDataErr as NetworkErrObj),
        false
      );
    } else if (fetchConfigDataError) {
      setIsLoading(false);
      displayErrorToast(
        (fetchConfigDataError as CommandServiceNetworkErrObj)?.status,
        (fetchConfigDataError as CommandServiceNetworkErrObj)?.data?.Error,
        true
      );
    } else if (downloadFileSasUriErr) {
      displayErrorToast(
        (downloadFileSasUriErr as NetworkErrObj)?.status,
        getErrorObjValue(downloadFileSasUriErr as NetworkErrObj),
        false
      );

      setFileToDownload("");
    }
  }, [configMetaDataErr, fetchConfigDataError, downloadFileSasUriErr]);

  const isLoadingFn = () => configMetaDataLoading || downloadSasUriLoading || isConfigMetaDataFetching || isDownloadFileSasUriFetching;

  return (
    <div className="config-data-container mb-4">
      {showToast && (
        /* istanbul ignore next */
        <div className="toast-container" onClick={() => setShowToast(false)}>
          <Toast id="config-metadata-toast"
            showClose={true} showDismiss={false}
            title={errorTitle} type={"error"}
            lifetime={5000} subtitle={""} content={errorMessage}
          />
        </div>
      )}
      <PageLoader data-testid="page-loader" isLoading={isLoadingFn()} />
      <Card className="mx-auto w-[70vw] min-w-full max-w-4xl">
        <CardHeader title={t(LOCALE_CONST.reportConfigData)}
          titleVariant="alt" className="items-center card-header-title"
          slotRight={(
            <Button variant="secondary"
              onClick={() => setShowFetchDataModal(true)}
              data-testid="fetch-config-data-button"
            >
              {t(LOCALE_CONST.reportFetchConfigData)}
            </Button>
          )}
        />
        <CardContent>
          <div className="mb-4">{t(LOCALE_CONST.reportConfigDataDesc)}</div>
          <div className="flex">
            <div className="w-1/4">{t(LOCALE_CONST.reportTriggerTime)}</div>
            <div className="w-2/3">
              {convertUTCStringtoFormattedDate(
                configMetaData?.lastExecutionDetails?.triggerTime || ""
              )}
            </div>
          </div>

          <Divider orientation="horizontal" subtitle=""
            subtitlePosition="left" type="inset" className="my-4"
          />

          <div className="flex h-10 mb-4">
            <div className="w-1/4">{t(LOCALE_CONST.reportStatus)}</div>
            <div className={`w-2/3 ${getStatusClass(status)}`}>
              {status || "-"}
            </div>
          </div>
          {configMetaData?.fileDetails ? (
            /* istanbul ignore next */
            <div>
              {configMetaData.fileDetails.map(
                (file: FileDetailsEntity, index: number) => (
                  <FileDownload key={index} file={file}
                    isInProgress={status === "Initiated"}
                    onDownloadClick={(file: FileProps) => setFileToDownload(file.fileName)}
                  />
                )
              )}
            </div>
          ) : (
            <div>{t(LOCALE_CONST.reportNoConfigFilesFound)}</div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        onConfirm={handleConfirm} title={t(LOCALE_CONST.reportFetchConfigData)}
        isOpen={showFetchDataModal} onClose={() => setShowFetchDataModal(false)}
        content={(
          <div className="diag-cont-div-cls">
            <p>{t(LOCALE_CONST.reportFetchConfigDataModalContent)}</p>
          </div>
        )}
      />
      {<PageLoader content="" isLoading={isLoading} />}
    </div>
  );
};

export default ConfigData;